import { useEffect, useRef, useState } from "react";
import type { EmailDocument } from "@edm/schema";
import { api, type RenderResult } from "../api/client";
import { useEditorStore } from "./store/editorStore";

const RENDER_DEBOUNCE_MS = 800;

type PreviewMode = "desktop" | "mobile" | "text" | "source";

export function PreviewPane({ document: doc }: { document: EmailDocument }) {
    const setValidation = useEditorStore((s) => s.setValidation);
    const [mode, setMode] = useState<PreviewMode>("desktop");
    const [dark, setDark] = useState(false);
    const [useSampleData, setUseSampleData] = useState(true);
    const [result, setResult] = useState<RenderResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            try {
                // Sample data comes from the server's merge-tag bundle.
                const r = await api.post<RenderResult>("/api/v1/render", {
                    document: doc,
                    mode: useSampleData ? "preview" : "production",
                });
                setResult(r);
                setError(null);
                setValidation(r.warnings);
            } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
            }
        }, RENDER_DEBOUNCE_MS);
        return () => {
            if (timer.current) clearTimeout(timer.current);
        };
    }, [doc, useSampleData, setValidation]);

    const modeBtn = (m: PreviewMode, label: string) => (
        <button
            type="button"
            className={`preview-mode-btn${mode === m ? " active" : ""}`}
            onClick={() => setMode(m)}
        >
            {label}
        </button>
    );

    return (
        <div className="preview-pane" data-testid="preview-pane">
            <div className="preview-toolbar">
                {modeBtn("desktop", "Desktop")}
                {modeBtn("mobile", "Mobile")}
                {modeBtn("text", "Text")}
                {modeBtn("source", "HTML")}
                {(mode === "desktop" || mode === "mobile") && (
                    <button
                        type="button"
                        className={`preview-mode-btn${dark ? " active" : ""}`}
                        onClick={() => setDark((v) => !v)}
                        title="Dark mode approximation — real client dark modes differ"
                    >
                        Dark≈
                    </button>
                )}
                <label className="preview-sample" title="Substitute sample merge-tag data">
                    <input
                        type="checkbox"
                        checked={useSampleData}
                        onChange={(e) => setUseSampleData(e.target.checked)}
                    />
                    Sample data
                </label>
                {result && (
                    <span className="preview-size">{(result.sizeBytes / 1024).toFixed(1)} KB</span>
                )}
            </div>
            {dark && (mode === "desktop" || mode === "mobile") && (
                <div className="preview-dark-note">
                    Approximation only — verify in real clients before relying on dark-mode look.
                </div>
            )}
            <div className={`preview-body${dark ? " dark" : ""}`}>
                {error && <div className="preview-error">Preview failed: {error}</div>}
                {result && (mode === "desktop" || mode === "mobile") && (
                    <iframe
                        title="Email preview"
                        className={`preview-frame ${mode}${dark ? " dark" : ""}`}
                        style={{ width: mode === "mobile" ? 375 : "100%" }}
                        srcDoc={result.html}
                        sandbox=""
                    />
                )}
                {result && mode === "text" && (
                    <pre className="preview-text">
                        {`Subject: ${result.subject}\nPreheader: ${result.preheader}\n\n${result.text}`}
                    </pre>
                )}
                {result && mode === "source" && (
                    <pre className="preview-text source">{result.html}</pre>
                )}
            </div>
        </div>
    );
}
